import { useEffect, useState } from 'react';
import { Button, Modal, Select, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetMonitoredObject } from '../Api/getData';
import { useFilterDetails } from './global.jsx';
import { TbShip, TbBuilding, TbFilter } from 'react-icons/tb';
import { HiSearch } from 'react-icons/hi';

const FilterObject = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { filterDetails } = useFilterDetails();

    const paramsKodeCabang = searchParams.get("KodeCabang") || "SGM";
    const paramsShipCode = searchParams.get("ShipCode") || "G2008";

    const [selectedValue, setSelectedValue] = useState({
        KodeCabang: paramsKodeCabang,
        ShipCode: paramsShipCode,
    });

    const [dataObject, setDataObject] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchDataObject = async () => {
            try {
                setIsLoading(true);
                const response = await GetMonitoredObject({
                    AccountId: paramsKodeCabang,
                    ObjectCode: paramsShipCode,
                });
                setDataObject(response);
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isModalOpen) {
            fetchDataObject();
        }
    }, [paramsKodeCabang, paramsShipCode, isModalOpen]);

    useEffect(() => {
        setSelectedValue({
            KodeCabang: paramsKodeCabang,
            ShipCode: paramsShipCode,
        });
    }, [paramsKodeCabang, paramsShipCode]);

    const ship = dataObject?.Ship || [];

    const selectedShip = ship.find((item) => item.ShipCode === selectedValue.ShipCode);
    const selectedBranch = dataObject?.Cabang?.find(
        (item) => item.KodeCabang === selectedShip?.KodeCabang
    );

    const handleOk = async () => {
        if (!selectedValue.ShipCode) return;

        setIsSubmitting(true);

        try {
            const KodeCabang = dataObject.Ship.find(
                (item) => item.ShipCode === selectedValue.ShipCode
            )?.KodeCabang;
            const dataCabang = dataObject.Cabang.find(
                (item) => item.KodeCabang === KodeCabang
            ) ?? dataObject?.Cabang?.[0];
            const dataShip = dataObject.Ship.find(
                (item) => item.ShipCode === selectedValue.ShipCode
            ) ?? dataObject?.Ship?.[0];

            const data = { ...dataCabang, ...dataShip };

            localStorage.setItem('Filter', JSON.stringify(data));
            window.dispatchEvent(new CustomEvent('filterUpdated'));

            const queryParams = new URLSearchParams({
                KodeCabang: data.KodeCabang,
                ShipCode: data.ShipCode,
            }).toString();
            navigate(`?${queryParams}`);
            setIsModalOpen(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setSelectedValue({
            KodeCabang: paramsKodeCabang,
            ShipCode: paramsShipCode,
        });
        setIsModalOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/15 hover:border-white/25 transition-all duration-200 group"
            >
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 group-hover:bg-white/15 transition-colors">
                    <TbShip className="w-4 h-4 text-sky-300" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Vessel</p>
                    <p className="text-sm text-white font-medium truncate">
                        {filterDetails?.ShipName || 'Select Vessel'}
                    </p>
                </div>
                <TbFilter className="w-4 h-4 text-gray-400 group-hover:text-white shrink-0 transition-colors" />
            </button>

            <Modal
                title={null}
                centered
                open={isModalOpen}
                onCancel={handleCancel}
                footer={null}
                width={480}
                destroyOnHidden
                className="filter-object-modal"
            >
                <div className="pt-2">
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                        <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                            <TbShip className="w-5 h-5 text-sky-600" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-800">Filter Vessel</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Select vessel to monitor</p>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="flex flex-col items-center gap-2" role="status" aria-live="polite">
                                <Spin />
                                <span className="text-xs text-gray-500">Loading vessels...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4 pt-4">
                            <div>
                                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    <TbShip className="w-3.5 h-3.5" />
                                    Vessel
                                </label>
                                <Select
                                    showSearch
                                    placeholder="Select vessel"
                                    value={selectedValue.ShipCode || undefined}
                                    onChange={(value) => setSelectedValue((prev) => ({ ...prev, ShipCode: value }))}
                                    className="w-full"
                                    optionFilterProp="label"
                                    options={ship.map((item) => ({
                                        value: item.ShipCode,
                                        label: item.ShipName,
                                    }))}
                                    optionRender={(option) => (
                                        <div className="flex items-center gap-2 py-0.5">
                                            <TbShip className="w-4 h-4 text-sky-500" />
                                            <span className="text-sm font-medium text-gray-700">{option.label}</span>
                                            <span className="text-xs text-gray-400 ml-auto">{option.value}</span>
                                        </div>
                                    )}
                                />
                            </div>

                            {selectedShip && (
                                <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-3">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                                            <TbBuilding className="w-4 h-4 text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Branch</p>
                                            <p className="text-sm font-semibold text-gray-800 truncate">
                                                {selectedBranch?.NamaCabang || '-'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                                            <TbShip className="w-4 h-4 text-sky-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Vessel Code</p>
                                            <p className="text-sm font-semibold text-gray-800 truncate">
                                                {selectedShip.ShipCode}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-4 border-t border-gray-100">
                        <Button
                            onClick={handleCancel}
                            className="h-10 px-5 rounded-xl border-gray-200"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleOk}
                            loading={isSubmitting}
                            disabled={!selectedValue.ShipCode || isLoading}
                            icon={<HiSearch className="w-4 h-4" />}
                            className="h-10 px-6 rounded-xl shadow-sm"
                        >
                            Apply Filter
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default FilterObject;
